// src/pages/usa/Utah.js
import "../../App.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import BackOnly from "../../components/BackOnly";
import ContactPopup from "../../components/ContactPopup";
import AffiliateBooking from "../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../components/affiliate/AffiliateTrip";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

// ⭐ STRICT COMMAND: Utah ALWAYS goes back to USA map
export const backRoute = "/map/usa";

export default function Utah() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly
  useEffect(() => {
    navigate(".", { state: { backRoute } });
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Moab / Utah</h1>

        <section className="travel-review">
          {/* LEFT COLUMN */}
          <div className="travel-text">
            <h2>Discover Moab / Utah</h2>

            <p>
              There’s a lot to discover, explore, admire, and experience in Utah. I still
              have many places left to visit — and one trip alone isn’t enough to see it
              all. This page reflects my experience from a recent journey through Utah. I’ll
              mention a few places that captured my attention and sparked my curiosity, in
              case you feel the same pull to explore. If you’re interested in Utah, feel
              free to check back — I’ll update this page as I visit new locations.
            </p>

            <p>
              Based on my own research, the most visited cities in Utah are Salt Lake City,
              Moab, and Kanab. Of course, data can change over time, but these three stood
              out during my planning and exploration.
            </p>

            <p>
              <strong>Salt Lake City:</strong> Sitting at approximately 4,265 feet (1,300
              meters), it experiences a semi‑arid climate with four distinct seasons.
              Summer highs average around 86°F (30°C), while winter lows dip to about 19°F
              (‑7°C). Nestled between the Wasatch Mountains and the Great Salt Lake, it’s
              known for dramatic seasonal shifts and crisp mountain air.
            </p>

            <p>
              <strong>Moab:</strong> Perched at roughly 4,026 feet (1,227 meters), Moab is
              famous for its red rock landscapes and proximity to Arches and Canyonlands
              National Parks. Summers are hot and dry, with highs around 95°F (35°C), while
              winters cool down to about 21°F (‑6°C). Spring and fall offer ideal conditions
              for outdoor adventures.
            </p>

            <p>
              <strong>Kanab:</strong> At about 4,970 feet (1,515 meters), Kanab has a desert
              climate with hot summers and mild winters. July averages around 92°F (33°C),
              and January lows reach about 23°F (‑5°C). Known as “Little Hollywood,” it
              serves as a gateway to Zion, Bryce Canyon, and the Grand Staircase.
            </p>

            <h3>🚗 Getting There</h3>

            <p>
              Utah offers countless places to explore, but for now, I’ll focus on Moab,
              Arches National Park, and the surrounding area. Once I’ve visited more
              locations, I’ll turn Utah into a modular page — similar to how I’ve structured
              Puerto Rico and Colorado.
            </p>

            <p>
              <strong>Flying:</strong> The closest airport to Moab is{" "}
              <em>Canyonlands Field Airport (CNY)</em>, about 18 miles north of town, with
              limited direct flights from Denver and Phoenix. You can also fly into{" "}
              <em>Salt Lake City (SLC)</em>, <em>Grand Junction (GJT)</em>, or{" "}
              <em>Denver (DEN)</em> and drive the rest of the way.
            </p>

            <p>
              <strong>Driving:</strong> Moab is accessible via US‑191 and is roughly four
              hours from Salt Lake City, five and a half hours from Denver, and one and a
              half hours from Grand Junction. The drive itself is part of the adventure,
              with scenic routes and optional stops at places like Colorado National
              Monument or Goblin Valley.
            </p>

            <p>
              <strong>Shuttle Services:</strong> If you’re flying into Grand Junction or
              Canyonlands Field, shuttle services like <em>Moab Express</em> can get you
              into town. Rental cars are highly recommended if you plan to explore the
              parks and trails at your own pace.
            </p>

            <h3>🌟 My Personal Observation</h3>

            <p>
              <strong>Arches National Park:</strong> A timed entry reservation is required
              during peak seasons. For 2025, reservations are needed from April 1 to July 6
              and again from August 28 to October 31, between 7 AM and 4 PM daily.
            </p>

            <p>
              Visitors with campground permits, backcountry or Fiery Furnace permits, or
              special authorizations are exempt from the timed entry requirement. Arriving
              by bike or on foot also bypasses the reservation system. Policies may change
              over time, so verify the latest guidelines directly with Arches National Park
              before your visit.
            </p>

            <p>
              Based on my opinion, here are the coordinates of the arches I consider the
              most beautiful.
            </p>

            {/* DEVILS GARDEN */}
            <h3>🏞️ Devils Garden Section</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Landscape Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.79058603776591,-109.60718915259645"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Tunnel Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.78541821302323,-109.59695992856942"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Pine Tree Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.78758543049428,-109.59866683289103"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Navajo Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.79145035076708,-109.60899110721114"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Partition Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.79209857864066,-109.60849373512293"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Double O Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.79920178731059,-109.62113731175103"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Private Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.80132026617879,-109.61600418023173"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Skyline Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.77552546343791,-109.5902807143981"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>
            </ul>

            {/* WINDOWS SECTION */}
            <h3>🌄 The Windows Section</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>North Window:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.685739287982926,-109.5328364166412"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>South Window:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.68520031654457,-109.53228149048783"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Turret Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.68432258241045,-109.5349107786598"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Double Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.69164386939991,-109.54038393076777"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>
            </ul>

            {/* SCENIC DRIVE */}
            <h3>🧭 Scenic Drive Section</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Delicate Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.743678385578974,-109.49929777890856"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Sand Dune Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.76440849033144,-109.58109570078692"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Broken Arch:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.77181626857496,-109.57770004120843"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Balanced Rock:</strong>
                  <br />
                  <a
                    href="https://maps.google.com/?q=38.700978150667744,-109.56454058636149"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>
            </ul>

            {/* CORONA ARCH */}
            <h3>🏜️ Corona Arch</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Corona Arch:</strong> Corona Arch is not located inside Arches
                  National Park. The hike is about three miles round trip on Bureau of Land
                  Management land. It is a beautiful, rewarding trail with impressive views
                  and a massive arch at the end.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=38.57992331712949,-109.62006337720696"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Corona Arch — View on Google Maps
                  </a>
                </div>
              </li>
            </ul>

            {/* HIDDEN GEMS */}
            <h3>🏔️ Hidden Gems in Moab</h3>

            <p>
              <strong>Moab Hidden Gems:</strong> Beyond the iconic arches and Corona Arch
              trail, Moab offers a treasure trove of lesser‑known adventures.{" "}
              <u>Mill Creek Canyon</u> is a lush desert oasis with waterfalls and swimming
              holes just minutes from downtown — perfect for cooling off or picnicking under
              cottonwoods. <u>Fisher Towers</u>, east of town, features towering red spires
              and a winding trail through surreal formations that feel straight out of a
              fantasy novel. Along <u>Potash Road</u>, you can find roadside{" "}
              <u>Dinosaur Tracks</u> and ancient <u>Petroglyphs</u>, all framed by the
              Colorado River. <u>Secret Spire</u> and <u>Dellenbaugh Tunnel</u> offer remote
              solitude and cinematic views for those with a four‑wheel‑drive spirit. If you
              are craving alpine air, the <u>La Sal Mountains Scenic Loop</u> delivers cool
              breezes, golden aspens, and a very different ecosystem — all within reach of
              red rock country.
            </p>

            {/* HOTELS */}
            <h3>🛏️ Places to Stay in Moab</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>My Place Hotel – Moab, UT:</strong> Clean, spacious rooms and
                  proximity to Arches National Park. Guests appreciate the kitchenettes,
                  friendly staff, and quiet location.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Moab Springs Ranch:</strong> Boutique property near the Colorado
                  River with private bungalows and shaded ponds.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Field Station Moab:</strong> Adventure‑focused hotel with gear
                  rentals, maps, and a community feel.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Hoodoo Moab, Curio Collection by Hilton:</strong> Upscale, modern
                  rooms, walkability, and on‑site dining.
                </div>
              </li>

              <li>
                <div className="place-block">
  <strong>Fairfield Inn & Suites by Marriott Moab:</strong> Riverfront
  views, family‑friendly amenities, and proximity to Arches.
</div>
</li>
</ul>

<h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
            <div className="affiliate-block">
              <AffiliateExpedia />
              <AffiliateBooking />
              <AffiliateTrip />
            </div>
</div>

{/* RIGHT COLUMN */}
<div className="travel-image-column">
  <img src="/images/usa/utah/delicatearchview1.jpg" alt="Delicate Arch view" />
  <img src="/images/usa/utah/daft.jpg" alt="Delicate Arch from top" />
  <img src="/images/usa/utah/delicatearchhandstand.jpg" alt="Delicate Arch" />
  <img src="/images/usa/utah/coronaarch.jpg" alt="Corona Arch" />
  <img src="/images/usa/utah/another2arch.jpg" alt="Arches from distance" />
  <img src="/images/usa/utah/another3arch.jpg" alt="Corona Arch from distance" />
  <img src="/images/usa/utah/anotherarch.jpg" alt="Arch on top" />
  <img src="/images/usa/utah/brokenarch.jpg" alt="Broken Arch" />
  <img src="/images/usa/utah/brokenarchfromdistance.jpg" alt="Broken Arch from distance" />
  <img src="/images/usa/utah/southwindow.jpg" alt="South Window" />
  <img src="/images/usa/utah/sanddunearch.jpg" alt="Sand Dune Arch" />
  <img src="/images/usa/utah/nasw.jpg" alt="North and South Windows" />
  <img src="/images/usa/utah/doublearch.jpg" alt="Double Arch" />
  <img src="/images/usa/utah/doubleoarch.jpg" alt="Double O Arch" />
  <img src="/images/usa/utah/turretarch.jpg" alt="Turret Arch" />
  <img src="/images/usa/utah/landscapearch.jpg" alt="Landscape Arch" />
  <img src="/images/usa/utah/tfca.jpg" alt="Trail for Corona Arch" />
  <img src="/images/usa/utah/balancedrock.jpg" alt="Balanced Rock" />
  <img src="/images/usa/utah/narrowtrail.jpg" alt="Narrow trail" />
  <img src="/images/usa/utah/gardenofrocks.jpg" alt="Garden of Rocks" />
  <img src="/images/usa/utah/towersofrocks.jpg" alt="Towers of rocks" />
  <img src="/images/usa/utah/thearch.jpg" alt="The Arch" />
</div>
</section>
</div>

<div className="email-wrapper">
  <button className="email-link" onClick={() => setOpen(true)}>
    contact@thefindingspot.com
  </button>
</div>

<ContactPopup isOpen={open} onClose={() => setOpen(false)} />

<Footer />
</>
);
}
